import React, { FunctionComponent, useEffect, useState } from "react";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    styled,
} from "@mui/material";

import {
    PriceRuleCategoryNames,
    priceRuleStatusNames,
} from "../../utils/enums";

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    min-width: 400px;
`;

export interface IFilterDialogProps {
    open: boolean;
    onClose: () => void;
    filters: {
        status?: number;
        category?: number;
        sortBy?: string;
        sortOrder?: string;
    };
    onApply: (filters: any) => void;
}

export const FilterDialog: FunctionComponent<IFilterDialogProps> = (
    props: IFilterDialogProps,
) => {
    const { open, onClose, filters, onApply } = props;

    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    const handleClear = () => {
        const clearedFilters = {
            status: undefined,
            category: undefined,
            sortBy: "createdAt",
            sortOrder: "DESC",
        };
        setLocalFilters(clearedFilters);
        onApply(clearedFilters);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Filter & Sort</DialogTitle>
            <StyledDialogContent>
                <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={localFilters.status ?? ""}
                        label="Status"
                        onChange={(event) =>
                            setLocalFilters({
                                ...localFilters,
                                status:
                                    event.target.value === ""
                                        ? undefined
                                        : Number(event.target.value),
                            })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(priceRuleStatusNames).map(
                            ([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ),
                        )}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={localFilters.category ?? ""}
                        label="Category"
                        onChange={(event) =>
                            setLocalFilters({
                                ...localFilters,
                                category:
                                    event.target.value === ""
                                        ? undefined
                                        : Number(event.target.value),
                            })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(PriceRuleCategoryNames).map(
                            ([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ),
                        )}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={localFilters.sortBy || "createdAt"}
                        label="Sort By"
                        onChange={(event) =>
                            setLocalFilters({
                                ...localFilters,
                                sortBy: event.target.value,
                            })
                        }>
                        <MenuItem value="createdAt">Created Date</MenuItem>
                        <MenuItem value="status">Status</MenuItem>
                        <MenuItem value="category">Category</MenuItem>
                        <MenuItem value="name">Name</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <InputLabel>Sort Order</InputLabel>
                    <Select
                        value={localFilters.sortOrder || "DESC"}
                        label="Sort Order"
                        onChange={(event) =>
                            setLocalFilters({
                                ...localFilters,
                                sortOrder: event.target.value,
                            })
                        }>
                        <MenuItem value="ASC">Ascending</MenuItem>
                        <MenuItem value="DESC">Descending</MenuItem>
                    </Select>
                </FormControl>
            </StyledDialogContent>
            <DialogActions>
                <Button onClick={handleClear}>Clear</Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleApply} variant="contained">
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};
